import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { EraControllerApi } from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";

interface UseGetErasParams {
  pagination: DGT_Pagination;
  gridSorting: GridSortItem[];
  billingProviderId?: string;
  payerId?: string;
  eraDateFrom?: string;
  eraDateTo?: string;
  reconciled?: boolean;
  isManualUpload?: boolean;
  searchKey?: string;
  enabled?: boolean;
}

export function useGetEras({
  pagination,
  gridSorting,
  billingProviderId,
  payerId,
  eraDateFrom,
  eraDateTo,
  reconciled,
  isManualUpload,
  searchKey,
  enabled = true,
}: UseGetErasParams): useBaseContent<any> {
  const prevPage = useRef<number>(pagination.page);
  // The search key has to reach the helper, otherwise a search started from any
  // page but the first keeps querying that stale page index and the matching
  // ERA - which sits on page one of the filtered result - is never returned.
  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const sortData = gridSorting?.[0];

  return useQuery({
    enabled,
    queryKey: [
      "get-all-eras",
      pageIndex,
      pagination.pageSize,
      sortData?.field,
      sortData?.sort,
      billingProviderId,
      payerId,
      eraDateFrom,
      eraDateTo,
      reconciled,
      isManualUpload,
      searchKey,
    ],
    // Required by getPageIndexWhileSearch: it reports page 0 for a search while
    // the grid still holds the old page, so the previous rows must stay until
    // the search answers.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,

    queryFn: async () =>
      new EraControllerApi(getConfigObject())
        .getAllEras(
          pageIndex,
          pagination.pageSize,
          sortData?.field ?? undefined,
          sortData?.sort ?? undefined,
          billingProviderId,
          payerId,
          eraDateFrom,
          eraDateTo,
          reconciled,
          isManualUpload,
          searchKey,
          getXtenantId()
        )
        .then((it) => it?.data),
  });
}
